import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FeaturedQuote } from "@/components/FeaturedQuote";
import { FeaturedBook } from "@/components/FeaturedBook";
import { MailchimpForm } from "@/components/MailchimpForm";

const YT_CHANNEL_URL = "https://www.youtube.com/@ministerjeb";
// Featured video — paste your YouTube video ID to embed it directly.
const YT_FEATURED_VIDEO_ID = "nFpDLK0hEpY";

const SITE = "https://mrbquotes.com";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Mr. B Quotes — Games, Short Stories, Glossary & More" },
      { name: "description", content: "Read. Play. Learn. Explore Mr. B Quotes for fresh ideas, short stories, quote games, and an expanding glossary designed to inspire, inform, and make you think." },
      { property: "og:title", content: "Mr. B Quotes — Games, Short Stories, Glossary & More" },
      { property: "og:description", content: "Read, play, and reflect with Mr. B Quotes — videos, games, stories, glossary, and more." },
      { property: "og:url", content: `${SITE}/` },
      { name: "twitter:title", content: "Mr. B Quotes" },
      { name: "twitter:description", content: "Inspire. Inform. Think, think, think." },
    ],

    links: [{ rel: "canonical", href: `${SITE}/` }],
  }),
  component: Home,
});

function Home() {
  return (
    <div className="min-h-screen">
      <SiteHeader active="home" />
      <main id="main">


      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-gold opacity-[0.07] blur-3xl" aria-hidden />
        <div className="relative mx-auto max-w-6xl px-4 pt-16 pb-20 sm:pt-24 sm:pb-28 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-gold" /> mrbquotes.com
          </div>
          <h1 className="mt-6 font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95]">
            Inspire. Inform.<br />
            <span className="text-gold italic">Think, think, think.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Mr. B Quotes</strong> is a home for the world's most thought-provoking words —
            curated for the curious, the reflective, and the seekers of meaning.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/games" className="rounded-full bg-gradient-gold px-6 py-3 text-sm font-semibold text-ink shadow-gold hover:opacity-90 transition">
              Play the Games
            </Link>
            <Link to="/stories" className="rounded-full border border-border px-6 py-3 text-sm font-medium hover:border-gold transition">
              Read the Stories
            </Link>
            <a href="#watch" className="rounded-full border border-border px-6 py-3 text-sm font-medium hover:border-gold transition">
              Watch latest
            </a>
          </div>
        </div>
      </section>

      {/* Featured Quote — signature daily quote with share */}
      <FeaturedQuote />

      {/* YouTube section — single, considered CTA */}
      <section id="watch" className="border-t border-border/60 bg-ink/40">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:py-20">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-center">
            <div className="relative">
              <div className="aspect-video rounded-2xl border border-border bg-card overflow-hidden shadow-elegant">
                {YT_FEATURED_VIDEO_ID ? (
                  <YouTubeFacade videoId={YT_FEATURED_VIDEO_ID} />
                ) : (
                  <a
                    href={YT_CHANNEL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex h-full w-full flex-col items-center justify-center gap-4 bg-gradient-to-br from-card to-ink p-8 text-center transition hover:from-gold/5"
                    aria-label="Open the Mr. B Quotes YouTube channel"
                  >
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gold text-ink shadow-gold">
                      <svg viewBox="0 0 24 24" className="h-7 w-7 ml-1" fill="currentColor" aria-hidden><path d="M8 5v14l11-7z" /></svg>
                    </div>
                    <div>
                      <div className="font-display text-2xl text-gold">@ministerjeb</div>
                      <div className="text-sm text-muted-foreground mt-1">Open the channel on YouTube</div>
                    </div>
                  </a>
                )}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">The Channel</div>
              <h2 className="mt-2 font-display text-4xl sm:text-5xl">Watch on YouTube</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                New quote videos every week — wisdom from the ancients, the moderns, and the voices in between.
                Subscribe to <strong className="text-foreground">Mr. B Quotes</strong> and never miss a reflection.
              </p>
              <div className="mt-6">
                <a
                  href={YT_CHANNEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#FF0000] px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition"
                >
                  <YouTubeIcon /> Subscribe on YouTube
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Book — Amazon */}
      <FeaturedBook />

      {/* Collections */}
      <section id="collections" className="border-t border-border/60">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:py-20">
          <div className="mb-10 text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">The Mr. B Quotes Universe</div>
            <h2 className="mt-2 font-display text-4xl sm:text-5xl">Explore the collections</h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <CollectionCard
              numeral="I"
              title="Games"
              tagline="Live now"
              body="Six free, addictive quote games — Who Said This?, Quote Wordle, Finish the Quote, and more. Built for fans, trivia nights, and livestreams."
              href="/games"
              cta="Open the games →"
              live
            />
            <CollectionCard
              numeral="II"
              title="Stories"
              tagline="New"
              body="Original short essays — small stories with long lessons. Perspective, resilience, time, and the discipline of clear thinking."
              href="/stories"
              cta="Read the stories →"
              live
            />
            <CollectionCard
              numeral="III"
              title="Glossary"
              tagline="Live"
              body="An in-depth glossary on quotation, attribution, punctuation, and rhetoric — plain English, real examples."
              href="/glossary"
              cta="Read the glossary →"
              live
            />
            <CollectionCard
              numeral="IV"
              title="Quote in Context"
              tagline="New"
              body="Where did that famous line really come from, what did it actually mean, and how is it misused today? Plain-language explainers for the world's most quoted words."
              href="/quotes"
              cta="Read the explainers →"
              live
            />
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section id="newsletter" className="border-t border-border/60 bg-ink/40">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:py-20">
          <MailchimpForm />
        </div>
      </section>

      {/* About strip */}
      <section id="about" className="border-t border-border/60">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:py-20 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">About</div>
          <h2 className="mt-2 font-display text-4xl sm:text-5xl">A home for thinking out loud.</h2>
          <p className="mt-6 text-muted-foreground leading-relaxed text-lg">
            Mr. B Quotes exists to inspire, inform, and make you think, think, think. We gather the world's most powerful words and put them where they belong —
            into your day, your feed, and your conversations.
          </p>
          <div className="mt-8">
            <Link to="/about" className="rounded-full border border-gold/40 bg-gold/5 px-6 py-3 text-sm text-gold hover:bg-gold/10 transition">
              Read the full story
            </Link>
          </div>
        </div>
      </section>

      </main>
      <SiteFooter />
    </div>
  );
}

function CollectionCard({ numeral, title, tagline, body, href, cta, live }: {
  numeral: string; title: string; tagline: string; body: string; href: string; cta: string; live?: boolean;
}) {
  const isInternal = href.startsWith("/") && !href.startsWith("//");
  const inner = (
    <>
      <div className="flex items-start justify-between gap-4">
        <div className="font-display text-5xl text-gold/40 group-hover:text-gold/80 transition-colors">{numeral}</div>
        <span className={`text-[10px] uppercase tracking-[0.2em] ${live ? "text-gold" : "text-muted-foreground"}`}>
          {live && <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold mr-1.5 align-middle" />}{tagline}
        </span>
      </div>
      <h3 className="mt-4 font-display text-2xl sm:text-3xl text-foreground">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
      <div className="mt-6 flex items-center justify-end">
        <span className="text-xs font-medium text-gold">{cta}</span>
      </div>
    </>
  );
  const cls = "group relative overflow-hidden rounded-2xl border border-border bg-card p-6 sm:p-8 transition-all hover:border-gold hover:shadow-elegant";
  if (isInternal) return <Link to={href} className={cls}>{inner}</Link>;
  return <a href={href} className={cls}>{inner}</a>;
}

function YouTubeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
      <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z" />
    </svg>
  );
}

/**
 * Click-to-load YouTube facade — avoids third-party cookies on page load.
 * Fixes the DevTools Issues panel warning from youtube-nocookie.com by
 * only mounting the iframe after an explicit user interaction.
 */
function YouTubeFacade({ videoId }: { videoId: string }) {
  const [active, setActive] = useState(false);
  const thumb = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

  if (active) {
    return (
      <iframe
        className="h-full w-full"
        src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
        title="Mr. B Quotes — Featured Video"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setActive(true)}
      className="group relative h-full w-full overflow-hidden"
      aria-label="Play the featured Mr. B Quotes video"
    >
      <img
        src={thumb}
        alt="Featured Mr. B Quotes video thumbnail"
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover transition group-hover:scale-[1.02]"
      />
      <span className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition" aria-hidden />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold text-ink shadow-gold transition group-hover:scale-105">
          <svg viewBox="0 0 24 24" className="h-7 w-7 ml-1" fill="currentColor" aria-hidden>
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      </span>
      <span className="sr-only">Load and play the featured video from YouTube</span>
    </button>
  );
}
